import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Preset color palettes for quick selection
const colorPresets = [
  { name: 'Blue & Gold', primary: '#011F5B', secondary: '#F2C100' },
  { name: 'Navy & Orange', primary: '#003366', secondary: '#FF6600' },
  { name: 'Crimson & White', primary: '#A51C30', secondary: '#FFFFFF' },
  { name: 'Cardinal & White', primary: '#8C1515', secondary: '#FFFFFF' },
  { name: 'Purple & Gold', primary: '#4B2E83', secondary: '#B7A57A' },
  { name: 'Black & Gold', primary: '#000000', secondary: '#CFB991' },
];

// Common location presets for quick selection
const locationPresets = [
  { name: 'Philadelphia, PA', lat: 39.9526, lon: -75.1652, usgsSiteId: '01474500', usgsSiteName: 'Schuylkill River at Philadelphia' },
  { name: 'Boston, MA', lat: 42.3601, lon: -71.0589 },
  { name: 'New York, NY', lat: 40.7128, lon: -74.0060 },
  { name: 'Washington, DC', lat: 38.9072, lon: -77.0369 },
  { name: 'Seattle, WA', lat: 47.6062, lon: -122.3321 },
  { name: 'San Francisco, CA', lat: 37.7749, lon: -122.4194 },
  { name: 'Chicago, IL', lat: 41.8781, lon: -87.6298 },
  { name: 'Austin, TX', lat: 30.2672, lon: -97.7431 },
];

export default function TeamSettings() {
  const { token, isAdmin } = useAuth();
  const { teamSettings, saveTeamSettings, loading } = useTheme();
  
  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e3a8a');
  const [secondaryColor, setSecondaryColor] = useState('#eab308');
  const [logoUrl, setLogoUrl] = useState('');
  const [weatherLat, setWeatherLat] = useState('');
  const [weatherLon, setWeatherLon] = useState('');
  const [weatherLocationName, setWeatherLocationName] = useState('');
  const [usgsSiteId, setUsgsSiteId] = useState('');
  const [usgsSiteName, setUsgsSiteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load current settings
  useEffect(() => {
    if (teamSettings) {
      setDisplayName(teamSettings.displayName || '');
      setPrimaryColor(teamSettings.primaryColor || '#1e3a8a');
      setSecondaryColor(teamSettings.secondaryColor || '#eab308');
      setLogoUrl(teamSettings.logoUrl || '');
      setWeatherLat(teamSettings.weatherLat?.toString() || '');
      setWeatherLon(teamSettings.weatherLon?.toString() || '');
      setWeatherLocationName(teamSettings.weatherLocationName || '');
      setUsgsSiteId(teamSettings.usgsSiteId || '');
      setUsgsSiteName(teamSettings.usgsSiteName || '');
    }
  }, [teamSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setSaving(true);

    const result = await saveTeamSettings(token, {
      displayName,
      primaryColor,
      secondaryColor,
      logoUrl: logoUrl || null,
      weatherLat: weatherLat ? parseFloat(weatherLat) : null,
      weatherLon: weatherLon ? parseFloat(weatherLon) : null,
      weatherLocationName: weatherLocationName || null,
      usgsSiteId: usgsSiteId || null,
      usgsSiteName: usgsSiteName || null
    });

    setSaving(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'Team settings saved successfully! The new theme is now active.' });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
  };

  const applyLocationPreset = (preset) => {
    setWeatherLat(preset.lat.toString());
    setWeatherLon(preset.lon.toString());
    setWeatherLocationName(preset.name);
    setUsgsSiteId(preset.usgsSiteId || '');
    setUsgsSiteName(preset.usgsSiteName || '');
  };

  const applyPreset = (preset) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
  };

  if (!isAdmin()) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Only team admins can access team settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-theme-primary mb-6">Club Settings</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Team Display Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Your Team Central"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
            maxLength={100}
          />
          <p className="mt-1 text-sm text-gray-500">
            This name appears in the header and throughout the app.
          </p>
        </div>

        {/* Color Presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Color Presets
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {colorPresets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => applyPreset(preset)}
                className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors text-left"
              >
                <div className="flex gap-1">
                  <div 
                    className="w-5 h-5 rounded-full border border-gray-300"
                    style={{ backgroundColor: preset.primary }}
                  />
                  <div 
                    className="w-5 h-5 rounded-full border border-gray-300"
                    style={{ backgroundColor: preset.secondary }}
                  />
                </div>
                <span className="text-xs text-gray-600 truncate">{preset.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-gray-300"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#1e3a8a"
                pattern="^#[0-9A-Fa-f]{6}$"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Used for headers, buttons, and main accents
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer border border-gray-300"
              />
              <input
                type="text"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                placeholder="#eab308"
                pattern="^#[0-9A-Fa-f]{6}$"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Used for navigation buttons in the header
            </p>
          </div>
        </div>

        {/* Preview */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preview
          </label>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            {/* Header Preview */}
            <div 
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: primaryColor }}
              >
              {/* Left: Menu button + Team name */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: secondaryColor, color: primaryColor }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <span className="font-bold text-white text-lg">
                  {displayName || 'Team Central'}
                </span>
              </div>
              
              {/* Right: Account button */}
                <button
                  type="button"
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ backgroundColor: secondaryColor, color: primaryColor }}
              >
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: primaryColor + '30' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-sm font-medium">Account</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                </button>
            </div>

            {/* Dropdown Menu Preview */}
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide font-medium">Dropdown Menu Preview</p>
              <div className="w-56 rounded-lg overflow-hidden shadow-md bg-white border border-gray-200">
                <div className="py-1">
                  <div 
                    className="px-4 py-2 text-sm font-medium text-white flex items-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Active Item
                  </div>
                  <div className="px-4 py-2 text-sm flex items-center gap-2 text-gray-700">
                    <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Menu Item
                  </div>
                  <div className="px-4 py-2 text-sm flex items-center gap-2 text-gray-700">
                    <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Another Item
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            This preview shows how your team's branding will appear in the navigation.
          </p>
        </div>

        {/* Logo URL (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo URL (optional)
          </label>
          <input
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
          />
          <p className="mt-1 text-sm text-gray-500">
            Enter a URL to your team's logo (PNG or SVG recommended)
          </p>
        </div>

        {/* Weather Location */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weather Location</h3>
          <p className="text-sm text-gray-500 mb-4">
            Configure the location for weather and water conditions shown on the Daily Workout page.
          </p>
          
          {/* Location Presets */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Location Presets
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {locationPresets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => applyLocationPreset(preset)}
                  className={`p-2 text-xs border rounded-lg hover:border-gray-400 transition-colors text-left ${
                    weatherLocationName === preset.name 
                      ? 'border-theme-primary bg-theme-bg-secondary' 
                      : 'border-gray-200'
                  }`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Location */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Name
              </label>
              <input
                type="text"
                value={weatherLocationName}
                onChange={(e) => setWeatherLocationName(e.target.value)}
                placeholder="e.g., Philadelphia, PA"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="number"
                step="0.0001"
                value={weatherLat}
                onChange={(e) => setWeatherLat(e.target.value)}
                placeholder="e.g., 39.9526"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="number"
                step="0.0001"
                value={weatherLon}
                onChange={(e) => setWeatherLon(e.target.value)}
                placeholder="e.g., -75.1652"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            You can find coordinates by searching your location on Google Maps and copying the latitude and longitude from the URL.
          </p>
        </div>

        {/* USGS water gauge */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">River Gauge (USGS)</h3>
          <p className="text-sm text-gray-500 mb-4">
            Optional. Used to pull live water temperature and flow for the Daily Workout page and result templates.
            Find a site number at{' '}
            <a
              href="https://waterdata.usgs.gov/nwis/rt"
              target="_blank"
              rel="noreferrer"
              className="text-theme-primary underline"
            >
              waterdata.usgs.gov
            </a>
            .
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                USGS Site ID
              </label>
              <input
                type="text"
                value={usgsSiteId}
                onChange={(e) => setUsgsSiteId(e.target.value)}
                placeholder="e.g., 01474500"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gauge Name
              </label>
              <input
                type="text"
                value={usgsSiteName}
                onChange={(e) => setUsgsSiteName(e.target.value)}
                placeholder="e.g., Schuylkill River at Philadelphia"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
              />
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            className="px-6 py-2 rounded-lg font-medium text-white btn-theme-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Club Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

